import { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';
import Button from '../../../components/ui/Button';

const TestimonialsSection = () => {
    const [activeTestimonial, setActiveTestimonial] = useState(0);
    const [isPlaying, setIsPlaying] = useState(false);

    const testimonials = [
        {
            id: 1,
            name: 'Hon. Kwame Asante',
            position: 'Chief Executive',
            assembly: 'Kumasi Metropolitan Assembly',
            image: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=150&h=150&fit=crop&crop=face',
            linkedinVerified: true,
            quote: `Civicrate has transformed our revenue collection completely. We've seen a 340% increase in revenue within just 6 months. The transparency it provides has restored citizen trust in our assembly operations.`,videoThumbnail: 'https://images.unsplash.com/photo-1560472354-b33ff0c44a43?w=400&h=300&fit=crop',
            metrics: {
                revenueIncrease: '340%',timeReduction: '85%',satisfactionScore: '94%'
            },
            beforeAfter: {
                before: 'Manual collection took 3 days, high leakage',after: 'Digital collection in 15 minutes, zero leakage'
            }
        },
        {
            id: 2,
            name: 'Mrs. Akosua Mensah',position: 'Finance Director',assembly: 'Accra Metropolitan Assembly',image: 'https://images.unsplash.com/photo-1494790108755-2616b612b786?w=150&h=150&fit=crop&crop=face',
            linkedinVerified: true,
            quote: `The offline capabilities are game-changing for our field collectors. Even in areas with poor network coverage, revenue collection continues seamlessly. Our accountability has improved dramatically.`,
            videoThumbnail: 'https://images.unsplash.com/photo-1551836022-deb4988cc6c0?w=400&h=300&fit=crop',
            metrics: {
                revenueIncrease: '280%',timeReduction: '78%',satisfactionScore: '91%'
            },
            beforeAfter: {
                before: 'Lost revenue due to network issues',after: 'Continuous collection with offline sync'
            }
        },
        {
            id: 3,
            name: 'Mr. Joseph Osei',position: 'Revenue Manager',assembly: 'Tamale Metropolitan Assembly',image: 'https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=150&h=150&fit=crop&crop=face',
            linkedinVerified: true,
            quote: `Citizens now prefer paying through Civicrate because it's convenient and transparent. Our customer satisfaction has reached 94%. The real-time reporting helps us make better decisions daily.`,
            videoThumbnail: 'https://images.unsplash.com/photo-1553877522-43269d4ea984?w=400&h=300&fit=crop',
            metrics: {
                revenueIncrease: '425%',
                timeReduction: '90%',
                satisfactionScore: '96%'
            },
            beforeAfter: {
                before: 'Citizens avoided paying due to long queues',
                after: 'Citizens pay conveniently from anywhere'
            }
        }
    ];

    useEffect(() => {
        const interval = setInterval(() => {
            if (!isPlaying) {
                setActiveTestimonial((prev) => (prev + 1) % testimonials?.length);
            }
        }, 8000);

        return () => clearInterval(interval);
    }, [isPlaying, testimonials?.length]);

    const currentTestimonial = testimonials?.[activeTestimonial];

    const handleVideoPlay = () => {
        setIsPlaying(true);
        // In a real implementation, this would trigger video playback
        setTimeout(() => setIsPlaying(false), 3000); // Simulate video end
    };

    return (
        <section id="testimonials" className="py-20 bg-gradient-to-br from-primary/5 to-accent/5">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                {/* Section Header */}
                <div className="text-center mb-16">
                    <h2 className="text-3xl sm:text-4xl font-bold text-foreground mb-4">
                        Success Stories from Ghana's Leading Assemblies
                    </h2>
                    <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
                        Hear directly from Chief Executives and Finance Directors who have transformed
                        their revenue operations with Civicrate.
                    </p>
                </div>

                <div className="grid lg:grid-cols-2 gap-12 items-center">
                    {/* Video/Image Section */}
                    <div className="relative">
                        <div className="relative rounded-2xl overflow-hidden shadow-2xl">
                            <Image
                                src={currentTestimonial?.videoThumbnail}
                                alt={`${currentTestimonial?.name} testimonial video`}
                                className="w-full h-80 object-cover"
                            />

                            {/* Video Play Overlay */}
                            <div className="absolute inset-0 bg-black/40 flex items-center justify-center">
                                <Button
                                    variant="default"
                                    size="lg"
                                    onClick={handleVideoPlay}
                                    iconName="Play"
                                    iconPosition="left"
                                    className="bg-white text-primary hover:bg-white/90"
                                >
                                    {isPlaying ? 'Playing...' : 'Watch Testimonial'}
                                </Button>
                            </div>

                            {/* Assembly Badge */}
                            <div className="absolute top-4 left-4 bg-white/90 backdrop-blur-sm rounded-lg px-3 py-2">
                                <p className="text-sm font-medium text-foreground">{currentTestimonial?.assembly}</p>
                            </div>

                            {/* LinkedIn Verification */}
                            {currentTestimonial?.linkedinVerified && (
                                <div className="absolute top-4 right-4 bg-primary rounded-lg p-2">
                                    <Icon name="Linkedin" size={20} color="white" />
                                </div>
                            )}
                        </div>

                        {/* Metrics Cards */}
                        <div className="grid grid-cols-3 gap-4 mt-6">
                            <div className="bg-white rounded-lg p-4 text-center shadow-md">
                                <div className="text-2xl font-bold text-success mb-1">
                                    {currentTestimonial?.metrics?.revenueIncrease}
                                </div>
                                <p className="text-xs text-muted-foreground">Revenue Increase</p>
                            </div>

                            <div className="bg-white rounded-lg p-4 text-center shadow-md">
                                <div className="text-2xl font-bold text-primary mb-1">
                                    {currentTestimonial?.metrics?.timeReduction}
                                </div>
                                <p className="text-xs text-muted-foreground">Time Reduction</p>
                            </div>

                            <div className="bg-white rounded-lg p-4 text-center shadow-md">
                                <div className="text-2xl font-bold text-accent mb-1">
                                    {currentTestimonial?.metrics?.satisfactionScore}
                                </div>
                                <p className="text-xs text-muted-foreground">Satisfaction</p>
                            </div>
                        </div>
                    </div>

                    {/* Testimonial Content */}
                    <div className="space-y-8">
                        {/* Quote */}
                        <div className="relative">
                            <Icon name="Quote" size={48} color="var(--color-primary)" className="absolute -top-4 -left-4 opacity-20" />
                            <blockquote className="text-xl text-foreground leading-relaxed pl-8">
                                "{currentTestimonial?.quote}"
                            </blockquote>
                        </div>

                        {/* Author Info */}
                        <div className="flex items-center space-x-4">
                            <Image
                                src={currentTestimonial?.image}
                                alt={currentTestimonial?.name}
                                className="w-16 h-16 rounded-full object-cover"
                            />
                            <div>
                                <h4 className="text-lg font-bold text-foreground">{currentTestimonial?.name}</h4>
                                <p className="text-primary font-medium">{currentTestimonial?.position}</p>
                                <p className="text-sm text-muted-foreground">{currentTestimonial?.assembly}</p>
                            </div>
                            {currentTestimonial?.linkedinVerified && (
                                <div className="flex items-center space-x-1 text-primary">
                                    <Icon name="CheckCircle" size={16} />
                                    <span className="text-sm font-medium">Verified</span>
                                </div>
                            )}
                        </div>

                        {/* Before/After Comparison */}
                        <div className="bg-white rounded-xl p-6 shadow-lg">
                            <h4 className="font-semibold text-foreground mb-4">Transformation Impact</h4>

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div className="space-y-2">
                                    <div className="flex items-center space-x-2">
                                        <Icon name="X" size={16} color="var(--color-error)" />
                                        <span className="text-sm font-medium text-error">Before Civicrate</span>
                                    </div>
                                    <p className="text-sm text-muted-foreground pl-6">
                                        {currentTestimonial?.beforeAfter?.before}
                                    </p>
                                </div>

                                <div className="space-y-2">
                                    <div className="flex items-center space-x-2">
                                        <Icon name="Check" size={16} color="var(--color-success)" />
                                        <span className="text-sm font-medium text-success">After Civicrate</span>
                                    </div>
                                    <p className="text-sm text-muted-foreground pl-6">
                                        {currentTestimonial?.beforeAfter?.after}
                                    </p>
                                </div>
                            </div>
                        </div>

                        {/* Navigation Dots */}
                        <div className="flex items-center justify-center space-x-3">
                            {testimonials?.map((_, index) => (
                                <button
                                    key={index}
                                    onClick={() => setActiveTestimonial(index)}
                                    className={`w-3 h-3 rounded-full transition-all duration-200 ${
                                        index === activeTestimonial
                                            ? 'bg-primary w-8' :'bg-border hover:bg-muted-foreground'
                                    }`}
                                />
                            ))}
                        </div>
                    </div>
                </div>

                {/* Additional Social Proof */}
                <div className="mt-16 bg-white rounded-2xl p-8 shadow-lg">
                    <div className="text-center mb-8">
                        <h3 className="text-2xl font-bold text-foreground mb-2">
                            Trusted by Leading Assemblies Across Ghana
                        </h3>
                        <p className="text-muted-foreground">
                            Join the growing network of digitally transformed local assemblies
                        </p>
                    </div>

                    <div className="grid grid-cols-2 md:grid-cols-4 gap-8 items-center opacity-60">
                        {[
                            'Kumasi Metropolitan Assembly',
                            'Accra Metropolitan Assembly',
                            'Tamale Metropolitan Assembly',
                            'Cape Coast Metropolitan Assembly'
                        ]?.map((assembly, index) => (
                            <div key={index} className="text-center">
                                <div className="w-16 h-16 bg-muted rounded-full flex items-center justify-center mx-auto mb-2">
                                    <Icon name="Building2" size={24} color="var(--color-muted-foreground)" />
                                </div>
                                <p className="text-sm font-medium text-muted-foreground">{assembly}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </div>
        </section>
    );
};

export default TestimonialsSection;